/**
 * Validade de receitas — dias restantes e urgência visual.
 */

export type PrescriptionValidityUrgency =
  | 'ok'
  | 'warning'
  | 'critical'
  | 'expired'
  | 'unknown';

export type PrescriptionValidity = {
  daysRemaining: number | null;
  urgency: PrescriptionValidityUrgency;
  label: string;
  expiresAt: Date | null;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Interpreta YYYY-MM-DD (ou Date/ISO) no calendário local, evitando shift de timezone. */
function toLocalDate(value: Date | string): Date {
  if (value instanceof Date) return value;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const month = Number(dateOnly[2]) - 1;
    const day = Number(dateOnly[3]);
    return new Date(year, month, day);
  }
  return new Date(value);
}

/** Dias de calendário restantes até a data de validade (0 = vence hoje). */
export function calendarDaysRemaining(
  expiresAt: Date | string,
  now: Date = new Date(),
): number {
  const end = startOfLocalDay(toLocalDate(expiresAt));
  const today = startOfLocalDay(now);
  return Math.round((end.getTime() - today.getTime()) / MS_PER_DAY);
}

export function getPrescriptionValidity(
  expiresAt?: string | Date | null,
  now: Date = new Date(),
): PrescriptionValidity {
  if (!expiresAt) {
    return {
      daysRemaining: null,
      urgency: 'unknown',
      label: 'Validade não informada',
      expiresAt: null,
    };
  }

  const expiryDate = toLocalDate(expiresAt);
  if (Number.isNaN(expiryDate.getTime())) {
    return {
      daysRemaining: null,
      urgency: 'unknown',
      label: 'Validade inválida',
      expiresAt: null,
    };
  }

  const daysRemaining = calendarDaysRemaining(expiryDate, now);

  if (daysRemaining < 0) {
    const overdue = Math.abs(daysRemaining);
    return {
      daysRemaining,
      urgency: 'expired',
      label: overdue === 1 ? 'Vencida há 1 dia' : `Vencida há ${overdue} dias`,
      expiresAt: expiryDate,
    };
  }

  if (daysRemaining === 0) {
    return {
      daysRemaining: 0,
      urgency: 'critical',
      label: 'Vence hoje',
      expiresAt: expiryDate,
    };
  }

  if (daysRemaining <= 3) {
    return {
      daysRemaining,
      urgency: 'critical',
      label: daysRemaining === 1 ? '1 dia restante' : `${daysRemaining} dias restantes`,
      expiresAt: expiryDate,
    };
  }

  if (daysRemaining <= 7) {
    return {
      daysRemaining,
      urgency: 'warning',
      label: `${daysRemaining} dias restantes`,
      expiresAt: expiryDate,
    };
  }

  return {
    daysRemaining,
    urgency: 'ok',
    label: `${daysRemaining} dias restantes`,
    expiresAt: expiryDate,
  };
}

export function isPrescriptionDocumentType(documentType?: string | null): boolean {
  const type = String(documentType || '').toLowerCase();
  return type === 'prescription' || type === 'recipe' || type === 'receita';
}

export function urgencyBadgeClass(urgency: PrescriptionValidityUrgency): string {
  switch (urgency) {
    case 'expired':
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
    case 'critical':
      return 'bg-orange-100 text-orange-900 dark:bg-orange-900/40 dark:text-orange-100';
    case 'warning':
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100';
    case 'ok':
      return 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-100';
    default:
      return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';
  }
}

export function urgencyCardClass(urgency: PrescriptionValidityUrgency): string {
  switch (urgency) {
    case 'expired':
      return 'border-red-200 bg-red-50/80 dark:border-red-900/50 dark:bg-red-950/20';
    case 'critical':
      return 'border-orange-200 bg-orange-50/70 dark:border-orange-900/40 dark:bg-orange-950/20';
    case 'warning':
      return 'border-amber-200 bg-amber-50/60 dark:border-amber-900/40 dark:bg-amber-950/15';
    default:
      return 'border-gray-200 bg-white dark:border-neutral-800 dark:bg-neutral-900';
  }
}
